import type { DashboardBridge } from '@/components/organisms/Dashboard/DashboardBridge'
import { connectChatStream, RateLimitError, type StreamEvent } from '@/services/api/domains/chatbot-stream.api'
import type {
  ChatAttachedFile,
  ChatAttachment,
  chatbotApi as ChatbotApiType,
  ChatEarlyTerminationReason,
  ChatMessageAttachment,
  ChatRequest,
  ChatResponse,
  ChatToolCall,
} from '@/services/api/domains/chatbot.api'
import { announceCapacityWrites } from '@/services/capacity/capacity-refresh'
import { announceFormsWrites } from '@/services/forms/forms-refresh'
import { announceGeocodingWrites } from '@/services/geocoding/geocoding-refresh'
import { announceLibraryWrites } from '@/services/library/library-refresh'
import { announceScriptsWrite } from '@/services/scripts/scripts-refresh'
import { announceSecurityWrites } from '@/services/security/security-refresh'
import { announceSettingsWrites } from '@/services/settings/settings-refresh'
import { assign, createMachine, type Sender } from 'xstate'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  toolCalls?: ChatToolCall[]
  usage?: ChatResponse['usage']
  continuationRounds?: number
  earlyTerminationReason?: ChatEarlyTerminationReason
  /** data: URL rendered inline beneath the bubble (capture-still). */
  image?: string
  /** What the user attached in the picker: pictures with their bytes, PDFs by name. */
  attachments?: ChatMessageAttachment[]
  /** Library files uploaded from the picker for this turn. */
  attachedFiles?: ChatAttachedFile[]
}

export interface ToolPillState {
  toolCallId: string
  name: string
  status: 'running' | 'done' | 'error'
  roundNumber: number
  resultSummary?: string
  durationMs?: number
}

export type ProviderVendor = 'claude' | 'openai' | 'ollama' | 'bedrock'

export interface RateLimitInfo {
  code: string
  resetAt: string | null
}

export interface ChatbotContext {
  elementId: string
  dashboardId?: string
  sessionId: string | null
  messages: ChatMessage[]
  pendingUserMessage: string | null
  pendingContext: ChatRequest['context'] | undefined
  /** Client-tool result(s) to send on the next turn (Task 16) — a continuation
   *  turn that must NOT append a user bubble. Cleared on MESSAGE_COMPLETE /
   *  MESSAGE_ERROR / CLEAR like the other pending* fields. */
  pendingToolResults: Array<{ toolCallId: string; result: unknown }> | null
  /** The run id echoed on a pendingToolResults continuation (Task 16). */
  runId: string | null
  /** The most recent `client_tool_request` frame (Task 17), parked here for the
   *  host to execute. The MACHINE deliberately does not execute it: running a
   *  client tool needs the browser (a router, the page's unsaved-work state, the
   *  signed-in user's entitlements), none of which belong in a pure machine, and
   *  the same machine also drives `DashboardChatbot`. The host watches this field,
   *  runs the calls, and dispatches ONE `SEND_TOOL_RESULT`. Null until the server
   *  asks for a client tool. */
  clientToolRequest: { runId: string; toolCalls: Array<{ id: string; name: string; input: unknown }> } | null
  /**
   * True from CLIENT_TOOL_REQUEST until its result reaches the machine (SEND_TOOL_RESULT) or the
   * request is abandoned. While true a new message is refused: it would clear the parked request,
   * the browser would drop the tool's result, and the server would get a new message while its run
   * still waits for one — "tool_use ids were found without tool_result blocks".
   */
  toolResultOutstanding: boolean
  /** A SEND_TOOL_RESULT that arrived while a turn was still in flight. The
   *  machine leaves `active` only on MESSAGE_COMPLETE, but the SSE reader awaits
   *  between CHUNKS and Lambda response streaming does not guarantee
   *  `client_tool_request` and `message_complete` land in the same chunk — so an
   *  executor resolving in a microtask CAN dispatch here mid-turn. XState v4
   *  drops an unhandled event silently, which would hang the run with no
   *  diagnostic; we park the payload instead and re-fire it on reaching `ready`
   *  (see the `always` transition there). Never re-invokes sendActor while the
   *  fetch is in flight. */
  deferredToolResult: { runId: string; toolResults: Array<{ toolCallId: string; result: unknown }> } | null
  /** Surface/context of the ORIGINATING turn, retained across the continuation.
   *  `pendingSurface`/`pendingContext` are cleared on MESSAGE_COMPLETE, so by the
   *  time the client tool result comes back they are gone — and `req.surface`
   *  feeds the SYSTEM PROMPT, so a surface-less continuation would run with a
   *  different prompt than the half it continues. */
  lastSurface?: ChatRequest['surface']
  lastContext?: ChatRequest['context']
  /** Surface descriptor for the in-flight turn (Task 3) — top-level on the
   *  wire, never nested inside pendingContext (see DashboardChatbot's
   *  assembleChatContext, which omits `context` entirely on context-less
   *  pages; nesting surface there would suppress it on exactly those pages).
   *  Optional (unlike the sibling `pendingContext`) so pre-existing machine
   *  test fixtures that build a base context without it keep compiling. */
  pendingSurface?: ChatRequest['surface']
  /** Most-recent captured still (data: URL). Attached to subsequent user turns
   *  so vision follow-ups work; cleared on CLEAR or replaced by a new capture. */
  activeStill: string | null
  /** Image to send on the in-flight turn (resolved from event.image ?? activeStill). */
  pendingImage: string | null
  /** Picker attachments for the in-flight turn ONLY — never carried to the next one, unlike a still.
   *  Optional so existing fixtures that build a context without them keep compiling. */
  pendingAttachments?: ChatAttachment[] | null
  pendingAttachedFiles?: ChatAttachedFile[] | null
  currentResponse: string
  currentToolCalls: ToolPillState[]
  currentRoundNumber: number | null
  streamingEnabled: boolean
  streamingRetryUsed: boolean
  /**
   * Generic transport / send error message OR a structured RateLimitInfo when
   * the public AI budget is exhausted (PR6-3). The machine's `rateLimited`
   * state stores the structured form so the banner UX can format the reset
   * time; CLEAR_ERROR transitions back to `ready` and clears this field.
   */
  error: string | RateLimitInfo | null
  usage: ChatResponse['usage'] | null
  /**
   * Null on the sitewide assistant surface — there is no DashboardBridge to
   * attach to (DashboardBridgeProvider is mounted per-dashboard, below the
   * app layout). The sitewide turn is restricted to an answer-only tool
   * allowlist, so `emitBridgeEvents`/`dispatchToolToBridge` never need to
   * dereference it in that mode (Task 8).
   */
  bridge: DashboardBridge | null
  api: typeof ChatbotApiType
  provider: ProviderVendor
  model: string
}

export type ChatbotEvent =
  | {
      type: 'SEND_MESSAGE'
      message: string
      context?: ChatRequest['context']
      surface?: ChatRequest['surface']
      image?: { dataUrl: string }
      attachments?: ChatAttachment[]
      attachedFiles?: ChatAttachedFile[]
    }
  | {
      type: 'SEND_TOOL_RESULT'
      runId: string
      toolResults: Array<{ toolCallId: string; result: unknown }>
    }
  | { type: 'RESUME_SESSION'; sessionId: string; messages: ChatMessage[] }
  | { type: 'PROVIDER_CONFIG_CHANGED'; provider: ProviderVendor; model: string }
  | { type: 'STREAM_START'; data: { sessionId: string; turnId: string; model: string } }
  | { type: 'ROUND_START'; data: { roundNumber: number } }
  | { type: 'ROUND_END'; data: { roundNumber: number; toolCallsCount: number } }
  | { type: 'TEXT_DELTA'; data: { delta: string } }
  | { type: 'TOOL_CALL_START'; data: { toolCallId: string; name: string; input: unknown; roundNumber: number } }
  | {
      type: 'TOOL_CALL_END'
      data: { toolCallId: string; status: 'success' | 'error'; resultSummary: string; durationMs: number }
    }
  | {
      type: 'CLIENT_TOOL_REQUEST'
      data: { runId: string; toolCalls: Array<{ id: string; name: string; input: unknown }> }
    }
  | { type: 'MESSAGE_COMPLETE'; data: { finalResponse: ChatResponse } }
  | { type: 'STREAM_ERROR'; error: unknown }
  | { type: 'MESSAGE_ERROR'; error: string | unknown }
  | { type: 'CANCEL' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR' }
  | { type: 'CLEAR_STILL' }

/**
 * The picker's attachments on the wire. A client-tool continuation sends none: it continues the run
 * the attachments already reached, and resending them would hand the model the same files twice.
 */
function attachmentFields(ctx: ChatbotContext): Pick<ChatRequest, 'attachments' | 'attachedFiles'> {
  if (ctx.pendingToolResults?.length) return {}

  return {
    ...(ctx.pendingAttachments?.length ? { attachments: ctx.pendingAttachments } : {}),
    ...(ctx.pendingAttachedFiles?.length ? { attachedFiles: ctx.pendingAttachedFiles } : {}),
  }
}

/** What the transcript keeps of an attachment: a picture's bytes for its thumbnail, a PDF's name. */
function toMessageAttachment(a: ChatAttachment): ChatMessageAttachment {
  return a.kind === 'image' ? { kind: 'image', src: a.dataUrl } : { kind: 'document', name: a.name }
}

function streamEventToMachineEvent(ev: StreamEvent): ChatbotEvent | null {
  switch (ev.event) {
    case 'message_start':
      return { type: 'STREAM_START', data: ev.data }
    case 'round_start':
      return { type: 'ROUND_START', data: ev.data }
    case 'round_end':
      return { type: 'ROUND_END', data: ev.data }
    case 'text_delta':
      return { type: 'TEXT_DELTA', data: ev.data }
    case 'tool_call_start':
      return { type: 'TOOL_CALL_START', data: ev.data }
    case 'tool_call_end':
      return { type: 'TOOL_CALL_END', data: ev.data }
    case 'client_tool_request':
      return { type: 'CLIENT_TOOL_REQUEST', data: ev.data }
    case 'message_complete':
      return { type: 'MESSAGE_COMPLETE', data: ev.data }
    case 'done':
      return null
    case 'error':
      return ev.data.code === 'user_cancelled' ? null : { type: 'STREAM_ERROR', error: new Error(ev.data.message) }
    default:
      return null
  }
}

export const chatbotMachine = createMachine<ChatbotContext, ChatbotEvent>(
  {
    id: 'chatbot',
    initial: 'idle',
    states: {
      idle: {
        on: {
          SEND_MESSAGE: {
            target: 'active',
            actions: ['appendUserMessage', 'setPendingMessage', 'resetStreamingState'],
          },
          SEND_TOOL_RESULT: {
            cond: 'hasToolResults',
            target: 'active',
            actions: ['setPendingToolResults', 'resetStreamingState'],
          },
          RESUME_SESSION: { target: 'ready', actions: 'hydrateSession' },
          CLEAR: { actions: 'clearMessages' },
          CLEAR_STILL: { actions: 'clearStill' },
          PROVIDER_CONFIG_CHANGED: { actions: 'updateProviderConfig' },
        },
      },
      active: {
        // Invoke at the parent so the actor survives sending → streaming transitions.
        invoke: {
          id: 'sendActor',
          src: 'sendActor',
        },
        initial: 'sending',
        on: {
          // Deferral, not a transition: an internal action leaves `active` (and
          // therefore the in-flight sendActor + its AbortController) untouched.
          SEND_TOOL_RESULT: { actions: 'deferToolResult' },
          CLIENT_TOOL_REQUEST: { actions: 'storeClientToolRequest' },
          MESSAGE_COMPLETE: {
            target: 'ready',
            actions: [
              'appendAssistantMessage',
              'emitBridgeEvents',
              'announceLibraryWrites',
              'announceGeocodingWrites',
              'announceFormsWrites',
              'announceScriptsWrites',
              'announceSecurityWrites',
              'announceCapacityWrites',
              'announceSettingsWrites',
              'storeSession',
            ],
          },
          // PR6-3: rate-limit (public_budget_exhausted) ends the turn and
          // routes to the rateLimited state — no buffered fallback (it would
          // also fail with 429 on the same daily counter).
          STREAM_ERROR: [
            { cond: 'isRateLimitError', target: 'rateLimited', actions: 'storeRateLimit' },
            // A turn carrying pendingToolResults must NOT retry — replaying it
            // would replay the client-side action the tool result completed.
            { cond: 'isClientToolTurn', target: 'ready', actions: 'setStreamErrorAsMessageError' },
            { target: 'active', actions: 'markStreamingRetryUsed', internal: false },
          ],
          MESSAGE_ERROR: [
            { cond: 'isRateLimitError', target: 'rateLimited', actions: 'storeRateLimit' },
            { target: 'ready', actions: 'setError' },
          ],
          // Target 'ready' exits 'active', which stops the invoked sendActor; its
          // cleanup (() => controller.abort()) aborts the fetch. No xsend needed.
          CANCEL: {
            target: 'ready',
            actions: 'appendCancelledAssistantMessage',
          },
        },
        states: {
          sending: {
            on: {
              STREAM_START: { target: 'streaming', actions: 'onStreamStart' },
            },
          },
          streaming: {
            on: {
              TEXT_DELTA: { actions: 'appendDelta' },
              ROUND_START: { actions: 'startRound' },
              ROUND_END: { actions: 'endRound' },
              TOOL_CALL_START: { actions: 'addToolPill' },
              TOOL_CALL_END: { actions: 'resolveToolPill' },
            },
          },
        },
      },
      ready: {
        // Exactly-once: `promoteDeferredToolResult` nulls `deferredToolResult` in
        // the SAME assign that moves to `active`, so the guard cannot match twice.
        // Every terminal action (error / cancel / rate-limit / clear / provider
        // change) also clears it, so a failed turn never resurrects a stale one.
        always: [
          {
            cond: 'hasDeferredToolResult',
            target: 'active',
            actions: ['promoteDeferredToolResult', 'resetStreamingState'],
          },
        ],
        on: {
          SEND_MESSAGE: {
            cond: 'noToolResultOutstanding',
            target: 'active',
            actions: ['appendUserMessage', 'setPendingMessage', 'resetStreamingState'],
          },
          SEND_TOOL_RESULT: {
            cond: 'hasToolResults',
            target: 'active',
            actions: ['setPendingToolResults', 'resetStreamingState'],
          },
          CANCEL: { cond: 'toolResultOutstanding', actions: 'abandonClientTool' },
          CLEAR: { actions: 'clearMessages' },
          CLEAR_STILL: { actions: 'clearStill' },
          PROVIDER_CONFIG_CHANGED: [
            { cond: 'hasMessages', target: 'ready', actions: 'resetForProviderChange', internal: false },
            { actions: 'updateProviderConfig' },
          ],
        },
      },
      // PR6-3: terminal state reached when the backend rejects with
      // public_budget_exhausted (HTTP 429). No automatic recovery — the user
      // must dismiss (CLEAR_ERROR) or wait until the reset time and reload.
      // We deliberately do NOT accept SEND_MESSAGE from here — the `Daily AI
      // budget reached` banner disables the input + send button on the
      // component side too.
      rateLimited: {
        on: {
          CLEAR_ERROR: { target: 'ready', actions: 'clearError' },
          CLEAR: { target: 'idle', actions: 'clearMessages' },
        },
      },
    },
  },
  {
    services: {
      sendActor: ctx => (sendBack: Sender<ChatbotEvent>, receive) => {
        const controller = new AbortController()
        const useStream = ctx.streamingEnabled && !ctx.streamingRetryUsed

        receive((msg: ChatbotEvent) => {
          if (msg.type === 'CANCEL') controller.abort()
        })
        ;(async () => {
          try {
            if (useStream) {
              await connectChatStream(
                {
                  elementId: ctx.elementId,
                  ...(ctx.dashboardId ? { dashboardId: ctx.dashboardId } : {}),
                  sessionId: ctx.sessionId ?? undefined,
                  // `pendingUserMessage` is '' (not undefined) on a client-tool
                  // continuation (Task 16) — omit it from the wire body rather
                  // than sending an empty string, which the backend rejects
                  // unless `toolResults` is present.
                  ...(ctx.pendingUserMessage ? { message: ctx.pendingUserMessage } : {}),
                  ...(ctx.pendingToolResults?.length ? { toolResults: ctx.pendingToolResults } : {}),
                  ...(ctx.runId ? { runId: ctx.runId } : {}),
                  context: ctx.pendingContext,
                  surface: ctx.pendingSurface,
                  provider: ctx.provider,
                  model: ctx.model,
                  ...(ctx.pendingImage ? { image: { dataUrl: ctx.pendingImage } } : {}),
                  ...attachmentFields(ctx),
                },
                controller.signal,
                ev => {
                  const m = streamEventToMachineEvent(ev)
                  if (m) sendBack(m)
                },
              )
            } else {
              const resp = await ctx.api.sendMessage({
                elementId: ctx.elementId,
                ...(ctx.dashboardId ? { dashboardId: ctx.dashboardId } : {}),
                sessionId: ctx.sessionId ?? undefined,
                ...(ctx.pendingUserMessage ? { message: ctx.pendingUserMessage } : {}),
                ...(ctx.pendingToolResults?.length ? { toolResults: ctx.pendingToolResults } : {}),
                ...(ctx.runId ? { runId: ctx.runId } : {}),
                context: ctx.pendingContext,
                surface: ctx.pendingSurface,
                provider: ctx.provider,
                model: ctx.model,
                ...(ctx.pendingImage ? { image: { dataUrl: ctx.pendingImage } } : {}),
                ...attachmentFields(ctx),
              })
              const final = resp.data
              sendBack({ type: 'MESSAGE_COMPLETE', data: { finalResponse: final } })
            }
          } catch (err) {
            if (useStream && !controller.signal.aborted) {
              sendBack({ type: 'STREAM_ERROR', error: err })
            } else if (controller.signal.aborted) {
              // User cancel — server emits message_complete before fetch rejects;
              // the reader loop in connectChatStream processes it. Suppress here.
            } else {
              sendBack({ type: 'MESSAGE_ERROR', error: err instanceof Error ? err.message : String(err) })
            }
          }
        })()

        return () => controller.abort()
      },
    },
    actions: {
      appendUserMessage: assign((ctx, event) => {
        if (event.type !== 'SEND_MESSAGE') return {}
        const img = event.image?.dataUrl ?? ctx.activeStill ?? undefined
        const msg: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: 'user',
          content: event.message,
          timestamp: Date.now(),
          ...(img ? { image: img } : {}),
          ...(event.attachments?.length ? { attachments: event.attachments.map(toMessageAttachment) } : {}),
          ...(event.attachedFiles?.length ? { attachedFiles: event.attachedFiles } : {}),
        }

        return {
          messages: [...ctx.messages, msg],
          error: null,
          ...(event.image?.dataUrl ? { activeStill: event.image.dataUrl } : {}),
        }
      }),
      // Task 16: writes the client-tool payload into CONTEXT (sendActor reads no
      // event, only ctx) and clears `error` the way appendUserMessage does for a
      // normal turn — WITHOUT appending a message bubble.
      setPendingToolResults: assign((ctx, event) => {
        if (event.type !== 'SEND_TOOL_RESULT') return {}
        // An empty array is truthy but sends `toolResults: []` AND omits
        // `message`, which the backend's superRefine rejects. Refuse it here.
        if (event.toolResults.length === 0) return {}

        return {
          toolResultOutstanding: false,
          pendingToolResults: event.toolResults,
          runId: event.runId,
          pendingUserMessage: '',
          // Preserve the originating turn's surface + context — the continuation
          // is the same run and must carry the same system prompt.
          pendingContext: ctx.lastContext,
          pendingSurface: ctx.lastSurface,
          error: null,
          deferredToolResult: null,
        }
      }),
      // Finding 4: MERGE rather than replace. Nothing in production dispatches
      // a second SEND_TOOL_RESULT mid-turn today, but the server rejects a
      // partial toolResults batch — if a future executor resolves multiple
      // client tools independently instead of batching them, replacing the
      // first deferred payload with the second would silently drop the first
      // tool's result and hang the run exactly like the original Critical did.
      // Merge by `toolCallId` (last write wins for a duplicate id, which the
      // server also rejects) so every distinct tool call survives to the one
      // continuation `promoteDeferredToolResult` fires.
      deferToolResult: assign((ctx, event) => {
        if (event.type !== 'SEND_TOOL_RESULT') return {}
        if (event.toolResults.length === 0) return {}

        const existing = ctx.deferredToolResult?.toolResults ?? []
        const merged = new Map(existing.map(r => [r.toolCallId, r]))
        for (const r of event.toolResults) merged.set(r.toolCallId, r)

        return {
          deferredToolResult: { runId: event.runId, toolResults: [...merged.values()] },
          toolResultOutstanding: false,
        }
      }),
      promoteDeferredToolResult: assign(ctx => {
        const deferred = ctx.deferredToolResult
        if (!deferred) return {}

        return {
          pendingToolResults: deferred.toolResults,
          runId: deferred.runId,
          pendingUserMessage: '',
          pendingContext: ctx.lastContext,
          pendingSurface: ctx.lastSurface,
          error: null,
          deferredToolResult: null,
        }
      }),
      setPendingMessage: assign((ctx, event) => {
        if (event.type !== 'SEND_MESSAGE') return {}

        return {
          pendingUserMessage: event.message,
          pendingContext: event.context,
          pendingSurface: event.surface,
          lastContext: event.context,
          lastSurface: event.surface,
          pendingImage: event.image?.dataUrl ?? ctx.activeStill ?? null,
          pendingAttachments: event.attachments?.length ? event.attachments : null,
          pendingAttachedFiles: event.attachedFiles?.length ? event.attachedFiles : null,
          // Defensive: an ordinary turn must never be mistaken for a client-tool
          // turn by `isClientToolTurn` if some future exit path forgets to clear.
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          // A fresh ordinary turn is not a client-tool turn; leaving a stale
          // parked request would make `isClientToolTurn` misread it as one.
          clientToolRequest: null,
          toolResultOutstanding: false,
        }
      }),
      abandonClientTool: assign(() => ({ clientToolRequest: null, toolResultOutstanding: false })),
      storeClientToolRequest: assign((_, event) => {
        if (event.type !== 'CLIENT_TOOL_REQUEST') return {}

        return {
          clientToolRequest: { runId: event.data.runId, toolCalls: event.data.toolCalls },
          toolResultOutstanding: true,
        }
      }),
      resetStreamingState: assign(() => ({
        currentResponse: '',
        currentToolCalls: [],
        currentRoundNumber: null,
      })),
      onStreamStart: assign((_, event) => {
        if (event.type !== 'STREAM_START') return {}

        return { sessionId: event.data.sessionId }
      }),
      appendDelta: assign((ctx, event) => {
        if (event.type !== 'TEXT_DELTA') return {}

        return { currentResponse: ctx.currentResponse + event.data.delta }
      }),
      startRound: assign((_, event) => {
        if (event.type !== 'ROUND_START') return {}

        return { currentRoundNumber: event.data.roundNumber }
      }),
      endRound: assign(() => ({})),
      addToolPill: assign((ctx, event) => {
        if (event.type !== 'TOOL_CALL_START') return {}
        const pill: ToolPillState = {
          toolCallId: event.data.toolCallId,
          name: event.data.name,
          status: 'running',
          roundNumber: event.data.roundNumber,
        }

        return { currentToolCalls: [...ctx.currentToolCalls, pill] }
      }),
      resolveToolPill: assign((ctx, event) => {
        if (event.type !== 'TOOL_CALL_END') return {}

        return {
          currentToolCalls: ctx.currentToolCalls.map(p =>
            p.toolCallId === event.data.toolCallId
              ? {
                  ...p,
                  status: event.data.status === 'success' ? ('done' as const) : ('error' as const),
                  resultSummary: event.data.resultSummary,
                  durationMs: event.data.durationMs,
                }
              : p,
          ),
        }
      }),
      appendAssistantMessage: assign((ctx, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return {}
        const fr = event.data.finalResponse
        const msg: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: fr.response,
          timestamp: Date.now(),
          toolCalls: fr.toolCalls,
          usage: fr.usage,
          continuationRounds: fr.continuationRounds,
          earlyTerminationReason: fr.earlyTerminationReason,
        }

        return {
          messages: [...ctx.messages, msg],
          pendingUserMessage: null,
          pendingToolResults: null,
          runId: null,
          // Finding 2: a tool-result continuation inherits `pendingImage` from
          // the ORIGINATING turn (setPendingToolResults/promoteDeferredToolResult
          // don't touch it, and nothing else clears it mid-turn) — without this,
          // the completed turn's still gets re-sent on the NEXT turn too, since
          // an ordinary SEND_MESSAGE's setPendingMessage only overwrites it when
          // dispatched, not retroactively. `activeStill` is a SEPARATE field
          // (the deliberate "latest still stays active for follow-ups" behavior)
          // and is NOT touched here.
          pendingImage: null,
          // Same for the picker's attachments: they belong to the turn that carried them.
          pendingAttachments: null,
          pendingAttachedFiles: null,
          // NOTE: `deferredToolResult` is deliberately NOT cleared here — a
          // result that arrived mid-turn is exactly what must survive into
          // `ready`, where the `always` transition fires it as one continuation.
          pendingContext: undefined,
          pendingSurface: undefined,
          currentResponse: '',
          currentToolCalls: [],
          currentRoundNumber: null,
          streamingRetryUsed: false,
          usage: fr.usage,
        }
      }),
      emitBridgeEvents: (ctx, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        for (const tc of event.data.finalResponse.toolCalls) {
          // No bridge on the sitewide surface (Task 8) — no-op rather than throw.
          if (ctx.bridge) dispatchToolToBridge(ctx.bridge, tc)
        }
      },
      announceLibraryWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceLibraryWrites(event.data.finalResponse.toolCalls ?? [])
      },
      announceGeocodingWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceGeocodingWrites(event.data.finalResponse.toolCalls ?? [])
      },
      announceFormsWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceFormsWrites(event.data.finalResponse.toolCalls ?? [])
      },
      announceScriptsWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceScriptsWrite(event.data.finalResponse.toolCalls ?? [])
      },
      announceSecurityWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceSecurityWrites(event.data.finalResponse.toolCalls ?? [])
      },
      announceCapacityWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceCapacityWrites(event.data.finalResponse.toolCalls ?? [])
      },
      announceSettingsWrites: (_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return
        announceSettingsWrites(event.data.finalResponse.toolCalls ?? [])
      },
      storeSession: assign((_, event) => {
        if (event.type !== 'MESSAGE_COMPLETE') return {}

        return { sessionId: event.data.finalResponse.sessionId }
      }),
      markStreamingRetryUsed: assign(() => ({ streamingRetryUsed: true })),
      // Task 16: a client-tool turn's STREAM_ERROR is terminal, not retryable —
      // surfaces the same way a MESSAGE_ERROR would, into `ready`.
      setStreamErrorAsMessageError: assign((_, event) => {
        if (event.type !== 'STREAM_ERROR') return {}

        return {
          error: event.error instanceof Error ? event.error.message : String(event.error),
          pendingUserMessage: null,
          pendingContext: undefined,
          pendingSurface: undefined,
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          clientToolRequest: null,
          toolResultOutstanding: false,
          currentResponse: '',
          currentToolCalls: [],
          currentRoundNumber: null,
        }
      }),
      appendCancelledAssistantMessage: assign(ctx => {
        // Synthesize a user_cancelled completion from partial state. The server's
        // own message_complete cannot reach the client once the fetch is aborted,
        // so we build one locally from the accumulated currentResponse.
        const partial = ctx.currentResponse.trim()
        const msg: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: partial || '(cancelled before any response)',
          timestamp: Date.now(),
          earlyTerminationReason: 'user_cancelled',
        }

        return {
          messages: [...ctx.messages, msg],
          pendingUserMessage: null,
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          // Task 20 Fix 1: a cancelled / failed / rate-limited turn must also
          // drop the parked client-tool request. Leaving it set lets the host's
          // in-flight executor dispatch SEND_TOOL_RESULT into `ready`, where
          // `hasToolResults` starts a NEW BILLED continuation turn after the
          // user pressed Stop.
          clientToolRequest: null,
          toolResultOutstanding: false,
          pendingContext: undefined,
          pendingSurface: undefined,
          currentResponse: '',
          currentToolCalls: [],
          currentRoundNumber: null,
          streamingRetryUsed: false,
        }
      }),
      setError: assign((_, event) => {
        if (event.type !== 'MESSAGE_ERROR') return {}

        return {
          error:
            typeof event.error === 'string'
              ? event.error
              : event.error instanceof Error
                ? event.error.message
                : String(event.error),
          pendingUserMessage: null,
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          // Task 20 Fix 1: a cancelled / failed / rate-limited turn must also
          // drop the parked client-tool request. Leaving it set lets the host's
          // in-flight executor dispatch SEND_TOOL_RESULT into `ready`, where
          // `hasToolResults` starts a NEW BILLED continuation turn after the
          // user pressed Stop.
          clientToolRequest: null,
          toolResultOutstanding: false,
          pendingContext: undefined,
          pendingSurface: undefined,
          currentResponse: '',
          currentToolCalls: [],
          currentRoundNumber: null,
        }
      }),
      // PR6-3: rate-limit storage + clear. `storeRateLimit` extracts
      // `{code, resetAt}` from the RateLimitError payload (regardless of
      // whether the error came in via STREAM_ERROR or MESSAGE_ERROR).
      storeRateLimit: assign((_, event) => {
        if (event.type !== 'STREAM_ERROR' && event.type !== 'MESSAGE_ERROR') return {}
        const err = event.error
        const rl =
          err instanceof RateLimitError
            ? { code: err.code, resetAt: err.resetAt }
            : { code: 'rate_limited', resetAt: null }

        return {
          error: rl as RateLimitInfo,
          pendingUserMessage: null,
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          // Task 20 Fix 1: a cancelled / failed / rate-limited turn must also
          // drop the parked client-tool request. Leaving it set lets the host's
          // in-flight executor dispatch SEND_TOOL_RESULT into `ready`, where
          // `hasToolResults` starts a NEW BILLED continuation turn after the
          // user pressed Stop.
          clientToolRequest: null,
          toolResultOutstanding: false,
          pendingContext: undefined,
          pendingSurface: undefined,
          currentResponse: '',
          currentToolCalls: [],
          currentRoundNumber: null,
          streamingRetryUsed: false,
        }
      }),
      clearError: assign(() => ({ error: null })),
      clearStill: assign(() => ({ activeStill: null, pendingImage: null })),
      clearMessages: assign(() => ({
        messages: [],
        sessionId: null,
        error: null,
        usage: null,
        pendingUserMessage: null,
        pendingToolResults: null,
        runId: null,
        deferredToolResult: null,
        clientToolRequest: null,
        toolResultOutstanding: false,
        pendingContext: undefined,
        pendingSurface: undefined,
        pendingImage: null,
        pendingAttachments: null,
        pendingAttachedFiles: null,
        activeStill: null,
        currentResponse: '',
        currentToolCalls: [],
        currentRoundNumber: null,
        streamingRetryUsed: false,
      })),
      hydrateSession: assign((_, ev) => {
        if (ev.type !== 'RESUME_SESSION') return {}

        return {
          sessionId: ev.sessionId,
          messages: ev.messages,
          error: null,
        }
      }),
      resetForProviderChange: assign((ctx, ev) => {
        if (ev.type !== 'PROVIDER_CONFIG_CHANGED') return ctx

        return {
          ...ctx,
          messages: [],
          sessionId: null,
          currentResponse: '',
          currentToolCalls: [],
          error: null,
          usage: null,
          provider: ev.provider,
          model: ev.model,
          streamingRetryUsed: false,
          pendingToolResults: null,
          runId: null,
          deferredToolResult: null,
          clientToolRequest: null,
          toolResultOutstanding: false,
        }
      }),
      updateProviderConfig: assign((ctx, ev) => {
        if (ev.type !== 'PROVIDER_CONFIG_CHANGED') return ctx

        return { ...ctx, provider: ev.provider, model: ev.model }
      }),
    },
    guards: {
      hasMessages: (ctx: ChatbotContext) => ctx.messages.length > 0,
      // PR6-3: route 429 (public_budget_exhausted) errors to the rateLimited
      // state instead of the buffered-fallback path. Both STREAM_ERROR and
      // MESSAGE_ERROR carry the original error on `event.error`.
      isRateLimitError: (_: ChatbotContext, event: ChatbotEvent) => {
        if (event.type !== 'STREAM_ERROR' && event.type !== 'MESSAGE_ERROR') return false

        return event.error instanceof RateLimitError
      },
      // Task 16: a turn carrying pendingToolResults must not retry on STREAM_ERROR.
      // Truthiness, not `!== null`: a partially-built context (older test
      // fixtures) leaves this `undefined`, and `!== null` would misread an
      // ordinary turn as a client-tool turn — the same hazard fixed below for
      // `hasDeferredToolResult`.
      // A CONTINUATION turn is identified by `pendingToolResults`; the
      // ORIGINATING turn is identified by `clientToolRequest` having been set
      // during it. Both halves matter: the originating turn parks a client
      // tool request the host has ALREADY started executing, so retrying it
      // re-bills the LLM turn AND lands on the buffered `/ai/chat` path, whose
      // `pendingClientToolCalls` nothing on the frontend reads. The browser
      // then reports the FIRST attempt's tool call ids, which no longer match
      // the retry's trailing row -> `400 unknown tool call`. Spec 4.2: a
      // client-tool turn is non-retryable, on BOTH halves of the exchange.
      isClientToolTurn: (ctx: ChatbotContext) => Boolean(ctx.pendingToolResults) || Boolean(ctx.clientToolRequest),
      // Truthiness, not `!== null`: a partially-built context (older test
      // fixtures, a future consumer) leaves this `undefined`, and `!== null`
      // would then fire an eventless transition into `active` on every entry to
      // `ready` — including a plain RESUME_SESSION.
      hasDeferredToolResult: (ctx: ChatbotContext) => Boolean(ctx.deferredToolResult),
      noToolResultOutstanding: (ctx: ChatbotContext) => !ctx.toolResultOutstanding,
      toolResultOutstanding: (ctx: ChatbotContext) => Boolean(ctx.toolResultOutstanding),
      // Finding 1: refuse an empty toolResults array at the transition level —
      // an action (setPendingToolResults) cannot refuse the transition itself,
      // so without this guard SEND_TOOL_RESULT([]) still enters `active` and
      // sendActor fires a request with neither `message` nor `toolResults`.
      hasToolResults: (_: ChatbotContext, event: ChatbotEvent) =>
        event.type === 'SEND_TOOL_RESULT' && event.toolResults.length > 0,
    },
  },
)

// This switch is one of THREE lock-step tool lists (server registry <->
// CHATBOT_TOOL_REGISTRY <-> this switch), the same pattern AUDIT_ACTIONS uses
// across its three sites. The mirror lock lives at
// apps/console/src/machines/__tests__/dispatchToolParity.spec.ts — it hardcodes
// this switch's case ids (a switch has no runtime reflection) AND drives this
// function directly with a fake bridge, so a removed case fails the test, not
// just a stale list. Add/remove a case here => update that spec's
// BRIDGE_DISPATCHED_TOOL_IDS / NO_BRIDGE_ACTION_TOOL_IDS in the same change.
/**
 * The bridge parameter is deliberately NON-nullable even though
 * `ChatbotContext.bridge` is nullable. The sole production caller
 * (`emitBridgeEvents`) guards with `if (ctx.bridge)`, so this function is only
 * ever reached with a real bridge — and widening the parameter instead would
 * push a null check into all 24 `bridge.emit` sites in the switch below for a
 * case that cannot occur.
 */
export function dispatchToolToBridge(
  bridge: DashboardBridge,
  call: { id: string; name: string; input: unknown; result: unknown },
): void {
  const result = (call.result ?? {}) as Record<string, unknown>

  switch (call.name) {
    case 'zoom_to_location': {
      const r = result as { center?: [number, number]; zoom?: number }
      if (r.center && typeof r.zoom === 'number') bridge.emit('zoom-to-location', { center: r.center, zoom: r.zoom })

      return
    }

    case 'query_features': {
      const r = result as {
        layerId?: string
        features?: Array<{ properties?: Record<string, unknown>; geometry?: GeoJSON.Geometry }>
      }
      if (r.layerId && r.features)
        bridge.emit('highlight-features', { layerId: r.layerId, features: r.features as never })

      return
    }

    case 'toggle_layer': {
      const r = result as { layerId?: string; visible?: boolean }
      if (r.layerId && typeof r.visible === 'boolean')
        bridge.emit('toggle-layer', { layerId: r.layerId, visible: r.visible })

      return
    }

    case 'zoom_to_feature': {
      const r = result as { sampleFeatures?: Array<{ geometry?: GeoJSON.Geometry }> }
      const g = r.sampleFeatures?.[0]?.geometry
      if (g) bridge.emit('zoom-to-feature', { geometry: g })

      return
    }

    case 'query_features_spatial': {
      const r = result as {
        layerId?: string
        resolvedCenter?: [number, number]
        radiusMeters?: number
        features?: Array<{ properties?: Record<string, unknown>; geometry?: GeoJSON.Geometry }>
      }
      if (r.resolvedCenter && typeof r.radiusMeters === 'number')
        bridge.emit('show-radius-circle', { center: r.resolvedCenter, radiusMeters: r.radiusMeters })
      if (r.layerId && r.features)
        bridge.emit('highlight-features', { layerId: r.layerId, features: r.features as never })

      return
    }

    case 'query_features_range':
    case 'query_features_top':
    case 'query_floor': {
      const r = result as {
        layerId?: string
        features?: Array<{ properties?: Record<string, unknown>; geometry?: GeoJSON.Geometry }>
      }
      if (r.layerId && r.features)
        bridge.emit('highlight-features', { layerId: r.layerId, features: r.features as never })

      return
    }

    case 'measure_distance': {
      const r = result as {
        start?: [number, number]
        end?: [number, number]
        distance?: number
        unit?: 'meters' | 'kilometers' | 'miles' | 'feet'
      }

      if (r.start && r.end && typeof r.distance === 'number' && r.unit) {
        bridge.emit('show-measurement', { start: r.start, end: r.end, distance: r.distance, unit: r.unit })
      }

      return
    }

    case 'clear_map_overlays':
      bridge.emit('clear-map-overlays', {})

      return

    case 'spatial_analysis': {
      const r = result as {
        bridgeAction?: string
        resultGeometry?: GeoJSON.Feature | GeoJSON.FeatureCollection
        operation?: 'buffer' | 'intersect' | 'union'
      }

      if (r?.bridgeAction === 'show-analysis-result' && r.resultGeometry && r.operation) {
        bridge.emit('show-analysis-result', { operation: r.operation, geometry: r.resultGeometry })
      }

      return
    }

    case 'refresh_element': {
      const r = result as { elementId?: string }
      if (r?.elementId) bridge.emit('refresh-element', { elementId: r.elementId })

      return
    }

    // Both write tools do their work server-side, but the page holds the element list in
    // memory: without this the user's screen is unchanged until they reload, while the
    // model has already said the element is there. Guarded on a real success so a refusal
    // (which carries success:false and often no elementId) never triggers a re-read.
    case 'add_dashboard_element': {
      const r = result as { success?: boolean; elementId?: string; placed?: boolean }

      if (r?.success && r.elementId) {
        bridge.emit('dashboard:elements-changed', { elementId: r.elementId, action: 'added' })
      }

      return
    }

    case 'configure_dashboard_element': {
      const r = result as { success?: boolean; elementId?: string }

      if (r?.success && r.elementId) {
        bridge.emit('dashboard:elements-changed', { elementId: r.elementId, action: 'configured' })
      }

      return
    }

    case 'set_view_type': {
      const r = result as { viewType?: '2d' | '3d'; preserveCenter?: boolean }

      if (r.viewType) {
        bridge.emit('scene:set-view-type', { viewType: r.viewType, preserveCenter: r.preserveCenter ?? true })
      }

      return
    }

    case 'set_camera': {
      const r = result as {
        camera?: { center?: [number, number]; altitude_m?: number; tilt?: number; heading?: number }
        animate?: boolean
      }

      if (r.camera) {
        bridge.emit('scene:set-camera', { camera: r.camera, animate: r.animate ?? true })
      }

      return
    }

    case 'set_environment': {
      const r = result as {
        env?: {
          timeOfDay?: 'sunrise' | 'morning' | 'noon' | 'afternoon' | 'sunset' | 'night'
          weather?: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'foggy'
          atmosphere?: 'on' | 'off'
          waterReflections?: boolean
          sunPosition?: { azimuth_deg: number; elevation_deg: number }
        }
      }

      if (r.env) {
        bridge.emit('scene:set-environment', { env: r.env })
      }

      return
    }

    case 'play_slide': {
      const r = result as { slideId?: string }

      if (r.slideId) {
        bridge.emit('scene:play-slide', { slideId: r.slideId })
      }

      return
    }

    case 'set_active_floor': {
      const r = result as { buildingId?: string; floorValue?: number }
      if (r.buildingId && typeof r.floorValue === 'number')
        bridge.emit('scene:set-active-floor', { buildingId: r.buildingId, floorValue: r.floorValue })

      return
    }

    case 'clear_active_floor': {
      const r = result as { buildingId?: string; error?: string }
      // Skip error results (ambiguous_building / building_not_found) — they carry no
      // buildingId, and emitting with buildingId:undefined would clear EVERY in-view
      // building (useFloorActionRegistration reads undefined as "clear all"). Mirror the
      // self-guard the sibling set_active_floor / query_floor cases already have.
      if (r.error) return
      bridge.emit('scene:clear-active-floor', { buildingId: r.buildingId })

      return
    }

    case 'set_layer_3d_fidelity': {
      const r = result as {
        layerName?: string
        fidelity?: {
          elevation_mode?: string
          elevation_offset_m?: number
          extrusion_field?: string
          extrusion_unit?: string
          edges_enabled?: boolean
          labels_enabled?: boolean
        }
      }

      if (r.layerName && r.fidelity) {
        bridge.emit('scene:set-layer-3d-fidelity', { layerName: r.layerName, fidelity: r.fidelity })
      }

      return
    }

    case 'capture_still': {
      const r = result as { elementId?: string }
      if (r.elementId) bridge.emit('media:capture-still', { elementId: r.elementId, requestId: call.id })

      return
    }

    case 'cctv_goto_playback': {
      const r = result as { elementId?: string; timestamp?: string }
      if (r.elementId && r.timestamp)
        bridge.emit('cctv:goto-playback', { elementId: r.elementId, timestamp: r.timestamp })

      return
    }

    case 'control_playback': {
      const r = result as { elementId?: string; command?: 'play' | 'pause' | 'live' }
      if (r.elementId && r.command) bridge.emit('media:playback', { elementId: r.elementId, command: r.command })

      return
    }

    case 'video_seek': {
      const r = result as { elementId?: string; seconds?: number }
      if (r.elementId && typeof r.seconds === 'number')
        bridge.emit('video:seek', { elementId: r.elementId, seconds: r.seconds })

      return
    }

    case 'cctv_ptz': {
      const r = result as {
        elementId?: string
        direction?: 'up' | 'down' | 'left' | 'right'
        amount?: 'small' | 'medium' | 'large'
      }
      if (r.elementId && r.direction)
        bridge.emit('cctv:ptz', { elementId: r.elementId, direction: r.direction, amount: r.amount })

      return
    }

    case 'cctv_select': {
      const r = result as { cameraId?: string; elementId?: string }
      if (r.cameraId) bridge.emit('cctv:select', { cameraId: r.cameraId, elementId: r.elementId })

      return
    }

    /*
     * Robot control (Mode 3). Each case reads the tool RESULT and self-guards on
     * the fields it needs — a refusal (`{success:false, error, message}`) carries
     * no `robotId`, so it falls through and emits nothing. That is the same
     * self-guard shape `clear_active_floor` needed for the same reason: an error
     * result must not be dispatched as if it were an instruction.
     *
     * The element re-validates every one of these against the robot's live
     * advertisement before anything reaches the wire — see `use-robot-chat-bridge.ts`.
     */
    case 'robot_speak': {
      const r = result as { robotId?: string; text?: string; language?: string }
      if (r.robotId && r.text)
        bridge.emit('robot:speak', {
          robotId: r.robotId,
          text: r.text,
          ...(r.language ? { language: r.language } : {}),
        })

      return
    }

    case 'robot_perform_action': {
      const r = result as { robotId?: string; kind?: 'gesture' | 'linkcraft' | 'face'; name?: string }
      if (r.robotId && r.kind && r.name)
        bridge.emit('robot:perform', { robotId: r.robotId, kind: r.kind, name: r.name })

      return
    }

    case 'robot_stop': {
      const r = result as { robotId?: string }
      if (r.robotId) bridge.emit('robot:stop-speaking', { robotId: r.robotId })

      return
    }

    // Read-only: they answer the model, they do not touch the robot.
    case 'list_robot_capabilities':
    case 'robot_get_status':
    case 'get_map_info':
    default:
      return
  }
}
